<template>
  <div class="pm-card scope-settings" qid="scope-settings">
    <div class="pm-card__head">
      <h3>{{ $t('projects.scope_tab.settings_title') }}</h3>
      <span class="grow"></span>
      <span class="rp-icon-btn"
            v-if="canEdit && !editing"
            @click="startEditing"
            qid="scope-settings-edit"><font-awesome-icon icon="pen"/></span>
    </div>

    <div class="rp-card-body">
      <!-- Which parts of each item the task asks about (records projects) -->
      <template v-if="options.length">
        <div class="scope-settings__label">{{ $t('projects.scope_tab.what_to_check') }}</div>
        <div class="scope-settings__options">
          <div class="scope-settings__option"
               v-for="(option, index) in (editing ? form.options : options)"
               :key="option.code"
               :class="{'on': option.value === 'Yes', 'editable': editing}"
               @click="editing && toggleOption(index)"
               qid="scope-settings-option">
            <span class="project-launcher__switch" :class="{'on': option.value === 'Yes'}" v-if="editing"></span>
            <font-awesome-icon v-else :icon="option.value === 'Yes' ? 'check-circle' : 'minus-circle'"/>
            {{ $t('tasks.values.' + $options.filters.slugify(option.title)) }}
          </div>
        </div>
      </template>

      <!-- Who gets the task for each item -->
      <div class="scope-settings__label">{{ $t('projects.scope_tab.assignees_title') }}</div>
      <div class="scope-settings__rules">
        <template v-if="!isValidation">
          <div class="scope-settings__rule"
               :class="{'on': current.owners, 'editable': editing}"
               @click="editing && (form.owners = !form.owners)"
               qid="scope-settings-owners">
            <span class="project-launcher__switch" :class="{'on': current.owners}"></span>
            <div>
              <b>{{ $t('projects.scope_tab.owners') }}</b>
              <span>{{ $t('projects.scope_tab.owners_help') }}</span>
            </div>
          </div>
          <div class="scope-settings__rule"
               :class="{'on': current.members, 'editable': editing}"
               @click="editing && (form.members = !form.members)"
               qid="scope-settings-members">
            <span class="project-launcher__switch" :class="{'on': current.members}"></span>
            <div>
              <b>{{ $t('projects.scope_tab.members') }}</b>
              <span>{{ $t('projects.scope_tab.members_help') }}</span>
            </div>
          </div>
        </template>
        <div class="scope-settings__rule scope-settings__rule--users">
          <font-awesome-icon icon="user-tag" class="scope-settings__rule-icon"/>
          <div class="grow">
            <b>{{ $t('projects.scope_tab.default_assignees') }}</b>
            <span>{{ $t('projects.scope_tab.default_assignees_help') }}</span>
            <multiselect v-if="editing"
                         class="mt-2"
                         v-model="form.users"
                         :options="users"
                         :multiple="true"
                         track-by="id"
                         label="name"
                         :placeholder="$t('projects.scope_tab.default_assignees')"
                         :select-label="$t('system.dropdown_select_label')"
                         :selected-label="$t('system.dropdown_selected_label')"
                         :deselect-label="$t('system.dropdown_deselect_label')"
                         @search-change="loadUsers"
                         qid="scope-settings-users"
            ></multiselect>
            <div class="mt-2" v-else-if="currentUsers.length"><Team :users="currentUsers"/></div>
          </div>
        </div>
      </div>

      <div class="rp-form-actions" v-if="editing">
        <b-button variant="secondary" @click="editing = false">{{ $t('projects.create.cancel') }}</b-button>
        <b-button variant="success"
                  class="ml-2"
                  :disabled="saving"
                  @click="save"
                  qid="scope-settings-save">{{ $t('tasks.modal.save') }}</b-button>
      </div>
    </div>
  </div>
</template>

<script>
  import piincHttp from '@/util/configureAxios.js'
  import Team from '@/views/Projects/Management/Team.vue';

  const MODEL_CLASSES = {
    process: 'App\\Process\\Models\\Process',
    activity: 'App\\Activity\\Models\\Activity',
    system: 'App\\System\\Models\\System',
    third_party: 'App\\Maintenance\\ThirdParty\\Models\\ThirdParty',
    policy: 'App\\Maintenance\\Policy\\Models\\Policy'
  };

  // Scope settings of a project: what the tasks ask about and who gets them; replaces ScopeTable and ControlScopeTable (US 9396)
  export default {
    name: 'ScopeSettings',
    components: {
      Team
    },
    props: {
      project: {type: Object, required: true},
      // 'control' edits the assignees of control projects' controls
      kind: {type: String, default: 'asset'},
      canEdit: {type: Boolean, default: false}
    },
    data: function() {
      return {
        editing: false,
        saving: false,
        users: [],
        form: {owners: false, members: false, users: [], options: []}
      }
    },
    computed: {
      getClient() {
        return this.$store.state.client;
      },
      isControl() {
        return this.kind === 'control';
      },
      isValidation() {
        return this.project.type === 'validation';
      },
      // The parts of an item an update or validation task asks about, with this project's choices
      options() {
        let source = this.project.type === 'update' ? this.project.updateScopeOptions : (this.isValidation ? this.project.validationScopeOptions : null);
        if (this.isControl || !source || !MODEL_CLASSES[this.project.scope]) {
          return [];
        }
        let saved = [];
        try {
          saved = this.project.scope_data ? JSON.parse(this.project.scope_data) : [];
        } catch (e) {
          saved = [];
        }
        return _.map(source[MODEL_CLASSES[this.project.scope]] || [], function(option) {
          let existing = _.find(saved, {code: option.code});
          return Object.assign({}, option, {value: existing ? existing.value : (option.value || 'No')});
        });
      },
      currentUsers() {
        return (this.isControl ? this.project.control_scope_users : this.project.scope_users) || [];
      },
      current() {
        if (this.editing) {
          return this.form;
        }
        return {
          owners: !!(this.isControl ? this.project.control_scope_owners : this.project.scope_owners),
          members: !!(this.isControl ? this.project.control_scope_members : this.project.scope_members)
        };
      }
    },
    methods: {
      startEditing() {
        this.form = {
          owners: !!(this.isControl ? this.project.control_scope_owners : this.project.scope_owners),
          members: !!(this.isControl ? this.project.control_scope_members : this.project.scope_members),
          users: this.currentUsers.slice(),
          options: _.cloneDeep(this.options)
        };
        this.loadUsers();
        this.editing = true;
      },
      toggleOption(index) {
        let option = this.form.options[index];
        option.value = option.value === 'Yes' ? 'No' : 'Yes';
      },
      loadUsers(query) {
        let roles = [this.permissions.ROLE_CLIENT_ADMIN, this.permissions.ROLE_PARTITION_ADMIN];
        if (this.isValidation) {
          roles.push(this.permissions.ROLE_VALIDATOR);
        }
        if (['control', 'security_measure', 'risk_assessment', 'bia_assessment'].indexOf(this.project.type) !== -1) {
          roles.push(this.permissions.ROLE_MEMBER, this.permissions.ROLE_VALIDATOR);
        }
        piincHttp.get('users', {params: {client: this.getClient.id, keyword: query || undefined, role_name: roles, per_page: 50}}).then((response) => {
          this.users = response.data.items;
        }, () => {});
      },
      save() {
        let users = _.map(this.form.users, 'id');
        let data = this.isControl
          ? {control_scope_users: users, control_scope_owners: this.form.owners ? 1 : 0, control_scope_members: this.form.members ? 1 : 0}
          : {scope_users: users, scope_owners: this.form.owners ? 1 : 0, scope_members: this.form.members ? 1 : 0};
        if (this.form.options.length) {
          data.scope_data = JSON.stringify(this.form.options);
        }
        this.saving = true;
        piincHttp.put('projects/' + this.project.slug, data).then((response) => {
          this.saving = false;
          this.editing = false;
          this.$toastr('success', this.$t('projects.update.success'));
          this.$emit('updated', response.data);
        }, (error) => {
          this.saving = false;
          this.$toastr('error', error.data && error.data.message ? error.data.message : this.$t('projects.launcher.error_generic'));
        });
      }
    }
  }
</script>
